import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CardMedia,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../../components/common/PageHeader';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';
import documentoService from '../../services/documentoService';

const Membretes = () => {
  const { showNotification } = useNotification();
  const fileInputRef = useRef(null);
  
  const [membretes, setMembretes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedMembrete, setSelectedMembrete] = useState(null);
  const [currentMembrete, setCurrentMembrete] = useState({
    nombre: '',
    tipo: 'Ambos',
    activo: true,
    imagen: null
  });
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  
  // Cargar membretes
  useEffect(() => {
    const fetchMembretes = async () => {
      setLoading(true);
      try {
        const response = await documentoService.getMembretes();
        setMembretes(response.data);
      } catch (error) {
        console.error('Error al cargar los membretes:', error);
        setError('Error al cargar los membretes. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMembretes();
  }, []);
  
  // Abrir diálogo para crear o editar membrete
  const handleOpenDialog = (membrete = null) => {
    if (membrete) {
      setCurrentMembrete({
        id: membrete.id,
        nombre: membrete.nombre,
        tipo: membrete.tipo,
        activo: membrete.activo === 1 || membrete.activo === true,
        imagen: null // No se carga la imagen existente, solo se mostrará en preview
      });
    } else {
      setCurrentMembrete({
        nombre: '',
        tipo: 'Ambos',
        activo: true,
        imagen: null
      });
    }
    setOpenDialog(true);
  };
  
  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentMembrete({
      nombre: '',
      tipo: 'Ambos',
      activo: true,
      imagen: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Abrir diálogo de confirmación para eliminar
  const handleOpenDeleteDialog = (membrete) => {
    setSelectedMembrete(membrete);
    setOpenDeleteDialog(true);
  };
  
  // Cerrar diálogo de confirmación
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedMembrete(null);
  };
  
  // Abrir diálogo de vista previa
  const handleOpenPreviewDialog = (membrete) => {
    setSelectedMembrete(membrete);
    setPreviewUrl(`${process.env.REACT_APP_API_URL || ''}${membrete.imagen}`);
    setOpenPreviewDialog(true);
  };
  
  // Cerrar diálogo de vista previa
  const handleClosePreviewDialog = () => {
    setOpenPreviewDialog(false);
    setSelectedMembrete(null);
    setPreviewUrl('');
  };
  
  // Manejar cambio en los campos del formulario
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file' && files.length > 0) {
      setCurrentMembrete({
        ...currentMembrete,
        imagen: files[0]
      });
    } else if (type === 'checkbox') {
      setCurrentMembrete({
        ...currentMembrete,
        [name]: checked
      });
    } else {
      setCurrentMembrete({
        ...currentMembrete,
        [name]: value
      });
    }
  };
  
  // Guardar membrete
  const handleSubmit = async () => {
    if (!currentMembrete.nombre) {
      setError('El nombre del membrete es obligatorio');
      return;
    }
    
    if (!currentMembrete.imagen && !currentMembrete.id) {
      setError('Debe seleccionar una imagen para el membrete');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('nombre', currentMembrete.nombre);
      formData.append('tipo', currentMembrete.tipo);
      formData.append('activo', currentMembrete.activo ? 1 : 0);
      
      if (currentMembrete.imagen) {
        formData.append('imagen', currentMembrete.imagen);
      }
      
      let response;
      
      if (currentMembrete.id) {
        response = await documentoService.actualizarMembrete(currentMembrete.id, formData);
        showNotification('Membrete actualizado correctamente', 'success');
      } else {
        response = await documentoService.crearMembrete(formData);
        showNotification('Membrete creado correctamente', 'success');
      }
      
      // Actualizar la lista de membretes
      const membretesResponse = await documentoService.getMembretes();
      setMembretes(membretesResponse.data);
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar el membrete:', error);
      setError('Error al guardar el membrete. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Eliminar membrete
  const handleDelete = async () => {
    if (!selectedMembrete) return;
    
    setSubmitting(true);
    
    try {
      await documentoService.deleteMembrete(selectedMembrete.id);
      showNotification('Membrete eliminado correctamente', 'success');
      
      // Actualizar la lista de membretes
      const membretesResponse = await documentoService.getMembretes();
      setMembretes(membretesResponse.data);
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error al eliminar el membrete:', error);
      showNotification('Error al eliminar el membrete', 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Container maxWidth="lg">
      <PageHeader
        title="Administración de Membretes"
        subtitle="Gestione los membretes para documentos"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Configuración', to: '/configuracion' },
          { text: 'Membretes' }
        ]}
      />
      
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Membrete
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : membretes.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" align="center" color="textSecondary">
              No hay membretes registrados
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {membretes.map((membrete) => (
            <Grid item xs={12} sm={6} md={4} key={membrete.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="180"
                  image={`${process.env.REACT_APP_API_URL || ''}${membrete.imagen}`}
                  alt={membrete.nombre}
                  sx={{ objectFit: 'contain', p: 1 }}
                />
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" component="div">
                      {membrete.nombre}
                    </Typography>
                    <Box>
                      <Tooltip title="Ver">
                        <IconButton onClick={() => handleOpenPreviewDialog(membrete)} size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => handleOpenDialog(membrete)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleOpenDeleteDialog(membrete)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Tipo: {membrete.tipo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Estado: {membrete.activo ? 'Activo' : 'Inactivo'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Diálogo para crear/editar membrete */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentMembrete.id ? 'Editar Membrete' : 'Nuevo Membrete'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3, mt: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="nombre"
                label="Nombre del Membrete"
                value={currentMembrete.nombre}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  name="tipo"
                  value={currentMembrete.tipo}
                  onChange={handleChange}
                  label="Tipo"
                >
                  <MenuItem value="Header">Encabezado</MenuItem>
                  <MenuItem value="Footer">Pie de página</MenuItem>
                  <MenuItem value="Ambos">Ambos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  name="activo"
                  value={currentMembrete.activo}
                  onChange={handleChange}
                  label="Estado"
                >
                  <MenuItem value={true}>Activo</MenuItem>
                  <MenuItem value={false}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                {currentMembrete.id ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  name="imagen"
                  onChange={handleChange}
                  ref={fileInputRef}
                />
              </Button>
              {currentMembrete.imagen && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Archivo seleccionado: {currentMembrete.imagen.name}
                </Typography>
              )}
              {currentMembrete.id && !currentMembrete.imagen && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Mantener imagen actual
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting || !currentMembrete.nombre}
          >
            {submitting ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de confirmación para eliminar */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Eliminar Membrete"
        content={`¿Está seguro que desea eliminar el membrete "${selectedMembrete?.nombre}"? Esta acción no se puede deshacer.`}
        isSubmitting={submitting}
      />
      
      {/* Diálogo para vista previa */}
      <Dialog open={openPreviewDialog} onClose={handleClosePreviewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Vista Previa: {selectedMembrete?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <img
              src={previewUrl}
              alt={selectedMembrete?.nombre}
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreviewDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Membretes;